import React, { useState, useEffect } from 'react';
import { getRequest } from '../../services/apiService';

const ProjectStatusTable = ({ selectedShip }) => {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    const fetchProjectStatus = async () => {
      setLoading(true);
      setStatusData(null);
      
      if (!selectedShip) {
        setLoading(false);
        return;
      }

      try {
        const response = await getRequest(
          `/transaction/project-status/?project_id=${selectedShip}`
        );
        
        if (response && typeof response === 'object') {
          setStatusData(response);
        }
      } catch (error) {
        console.error("Error fetching project status:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjectStatus();
  }, [selectedShip]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus]);

  // Prepare status summary data
  const summaryData = statusData
  ? Object.entries(statusData).map(([status, info]) => ({
      status,
      count: info?.count || 0,
      tasks: info?.tasks || [],
      statusColor:
        status === 'On Track'
          ? 'bg-green-500'
          : status === 'At Risk'
          ? 'bg-red-500'
          : status === 'Delayed'
          ? 'bg-yellow-500'
          : status === 'Completed'
          ? 'bg-blue-500'
          : 'bg-gray-500',
      percentage:
        Object.values(statusData).reduce((total, curr) => total + (curr?.count || 0), 0) > 0
          ? Math.round(
              ((info?.count || 0) /
                Object.values(statusData).reduce(
                  (total, curr) => total + (curr?.count || 0),
                  0
                )) * 100
            )
          : 0,
    }))
  : [];


  // Get tasks for selected status
  const tasks = selectedStatus && statusData?.[selectedStatus]?.tasks || [];
  const totalPages = Math.ceil(tasks.length / rowsPerPage);
  const paginatedTasks = tasks.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Handle no ship selected
  if (!selectedShip) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500">
        Please select a ship to view project status details
      </div>
    );
  }

  // Handle no data
  if (!statusData || Object.keys(statusData).length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500">
        No project status data available for the selected ship
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
        <h4 className="text-sm font-medium text-indigo-800 mb-2">Project Status Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {summaryData.map((status, index) => (
            <div 
              key={index} 
              className={`bg-white p-3 rounded shadow-sm cursor-pointer transition-all duration-200 ${
                selectedStatus === status.status ? 'ring-2 ring-indigo-500' : 'hover:shadow-md'
              }`}
              onClick={() => setSelectedStatus(selectedStatus === status.status ? null : status.status)}
            >
              <div className="flex items-center mb-2">
                <div className={`h-3 w-3 rounded-full mr-2 ${status.statusColor}`}></div>
                <p className="text-xs text-gray-500">{status.status}</p>
              </div>
              <p className="text-lg font-semibold text-indigo-600">{status.count}</p>
              <p className="text-sm text-gray-600">{status.percentage}%</p>
            </div>
          ))}
        </div>
      </div>

      {selectedStatus && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedStatus} Tasks ({tasks.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task ID</th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Milestone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion %</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTasks.map((task, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{task.id}</td> */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{task.task__name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{task.milestone__title || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{task.start_date || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{task.end_date || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {task.actual_completion_percent !== null ? (
                        <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold text-xs">
                          {task.actual_completion_percent.toFixed(2)}%
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-between items-center p-4 border-t border-gray-100">
              <button
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectStatusTable;